import { supabase } from '@/integrations/supabase/client';

export type RoleType = 'team_owner' | 'coach' | 'venue';
export type AgreementType = 'platform_participation' | 'brand_usage' | 'certification_terms';

export interface CertificationAgreement {
  id: string;
  user_id: string;
  user_type: 'supabase' | 'internal';
  role_type: RoleType;
  platform_participation_accepted: boolean;
  brand_usage_accepted: boolean;
  certification_terms_accepted: boolean;
  platform_participation_version: string | null;
  brand_usage_version: string | null;
  certification_terms_version: string | null;
  accepted_at: string;
  created_at: string;
  updated_at: string;
}

export interface AgreementVersion {
  id: string;
  agreement_type: AgreementType;
  version: string;
  effective_date: string;
  content: string;
  is_current: boolean;
}

export interface SubmitCertificationAgreementData {
  userId: string;
  userType: 'supabase' | 'internal';
  roleType: RoleType;
  platformParticipationAccepted: boolean;
  brandUsageAccepted: boolean;
  certificationTermsAccepted: boolean;
}

class CertificationAgreementService {
  /**
   * Check if a user has already accepted certification agreements for a specific role
   */
  async checkAgreementStatus(
    userId: string,
    userType: 'supabase' | 'internal',
    roleType: RoleType
  ): Promise<CertificationAgreement | null> {
    const { data, error } = await supabase
      .from('role_certification_agreements')
      .select('*')
      .eq('user_id', userId)
      .eq('user_type', userType)
      .eq('role_type', roleType)
      .eq('platform_participation_accepted', true)
      .eq('brand_usage_accepted', true)
      .eq('certification_terms_accepted', true)
      .maybeSingle();

    if (error) {
      console.error('Error checking certification agreement status:', error);
      return null;
    }

    return data as CertificationAgreement | null;
  }

  /**
   * Get the current version of all agreements
   */
  async getCurrentAgreementVersions(): Promise<AgreementVersion[]> {
    const { data, error } = await supabase
      .from('certification_agreement_versions')
      .select('*')
      .eq('is_current', true);

    if (error) {
      console.error('Error fetching agreement versions:', error);
      return [];
    }

    return data as AgreementVersion[];
  }

  /**
   * Get a specific agreement content by type
   */
  async getAgreementContent(agreementType: AgreementType): Promise<AgreementVersion | null> {
    const { data, error } = await supabase
      .from('certification_agreement_versions')
      .select('*')
      .eq('agreement_type', agreementType)
      .eq('is_current', true)
      .single();

    if (error) {
      console.error('Error fetching agreement content:', error);
      return null;
    }

    return data as AgreementVersion;
  }

  /**
   * Submit certification agreement acceptance
   */
  async submitAgreement(data: SubmitCertificationAgreementData): Promise<{ success: boolean; error?: string }> {
    try {
      // Get current agreement versions
      const versions = await this.getCurrentAgreementVersions();
      const platformVersion = versions.find(v => v.agreement_type === 'platform_participation')?.version || '1.0';
      const brandVersion = versions.find(v => v.agreement_type === 'brand_usage')?.version || '1.0';
      const certificationVersion = versions.find(v => v.agreement_type === 'certification_terms')?.version || '1.0';

      const { error } = await supabase
        .from('role_certification_agreements')
        .insert({
          user_id: data.userId,
          user_type: data.userType,
          role_type: data.roleType,
          platform_participation_accepted: data.platformParticipationAccepted,
          brand_usage_accepted: data.brandUsageAccepted,
          certification_terms_accepted: data.certificationTermsAccepted,
          platform_participation_version: platformVersion,
          brand_usage_version: brandVersion,
          certification_terms_version: certificationVersion,
          accepted_at: new Date().toISOString(),
          ip_address: null, // Could be captured server-side
          user_agent: navigator.userAgent
        });

      if (error) {
        console.error('Error submitting certification agreement:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error: any) {
      console.error('Error submitting certification agreement:', error);
      return { success: false, error: error.message };
    }
  }
}

export const certificationAgreementService = new CertificationAgreementService();
